import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useReducedMotion } from 'framer-motion';
import { fmtNum, fmtDayShort } from '../pages/kyc-stats/components/format';
import ChartTooltip from '../pages/kyc-stats/components/ChartTooltip';

/*
 * UsageChart — a single-series, monochrome (ink/white) area chart for a 30-day
 * daily count series [{ day:'YYYY-MM-DD', count }]. Same visual register as the
 * kyc-stats ThroughputTimeline: hairline grid, ink line, soft gradient fill,
 * minimal axes, shared ChartTooltip. Responsive width (fits its container with
 * no horizontal scroll).
 */

const useIsDark = () => {
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(el.classList.contains('dark')));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <ChartTooltip
      title={fmtDayShort(p.day)}
      rows={[{ label: 'Tool calls', value: fmtNum(p.count), dotClass: 'bg-[#111111] dark:bg-white' }]}
    />
  );
};

export default function UsageChart({ data = [], height = 200 }) {
  const dark = useIsDark();
  const reduce = useReducedMotion();

  const axis = dark ? 'rgba(255,255,255,0.4)' : '#707072';
  const grid = dark ? 'rgba(255,255,255,0.08)' : '#e5e5e5';
  const inkLine = dark ? 'rgba(255,255,255,0.9)' : '#111111';
  const gradTop = dark ? 'rgba(255,255,255,0.22)' : 'rgba(17,17,17,0.16)';
  const gradBot = dark ? 'rgba(255,255,255,0)' : 'rgba(17,17,17,0)';

  if (!data.length) {
    return (
      <div className="relative h-48 flex items-center justify-center rounded-sm border border-[#e5e5e5] dark:border-white/[0.06]">
        <div className="absolute left-2 right-2 top-1/2 h-px bg-[#e5e5e5] dark:bg-white/[0.08]" />
        <span className="relative text-[11px] uppercase tracking-[0.28em] font-bold text-[#9e9ea0] dark:text-white/30">
          No usage in range
        </span>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -14 }}>
        <defs>
          <linearGradient id="acpUsageFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={gradTop} />
            <stop offset="100%" stopColor={gradBot} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={fmtDayShort}
          tick={{ fill: axis, fontSize: 10 }}
          stroke={grid}
          tickLine={false}
          minTickGap={28}
        />
        <YAxis
          tick={{ fill: axis, fontSize: 10 }}
          stroke={grid}
          width={34}
          tickLine={false}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: axis, strokeDasharray: '3 3' }} />
        <Area
          type="monotone"
          dataKey="count"
          stroke={inkLine}
          strokeWidth={2}
          fill="url(#acpUsageFill)"
          dot={false}
          activeDot={{ r: 3, fill: inkLine, stroke: 'none' }}
          isAnimationActive={!reduce}
          animationDuration={600}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
